import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Loader2, ChevronDown } from 'lucide-react';

export function InternalCompanySearchModal({ isOpen, onOpenChange }) {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);

  // Plan overview state
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expandedPlanDropdown, setExpandedPlanDropdown] = useState(false);

  // Cargar overview cuando el modal se abre
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const loadPlanOverview = async () => {
      setLoadingOverview(true);
      setOverviewError(null);
      setOverview(null);

      try {
        const functions = getFunctions();
        const getPlanOverview = httpsCallable(functions, 'internalGetPlanOverview');
        const response = await getPlanOverview({});

        if (response.data) {
          setOverview(response.data);
          setSelectedPlan(null);
          setExpandedPlanDropdown(false);
        }
      } catch (err) {
        console.error('[InternalCompanySearchModal] Error loading overview:', err);
        setOverviewError('No se pudo cargar el resumen de planes.');
      } finally {
        setLoadingOverview(false);
      }
    };

    loadPlanOverview();
  }, [isOpen]);

  const handleSearch = async () => {
    const trimmedQuery = search.trim();
    if (!trimmedQuery) {
      setError('Debes indicar una búsqueda.');
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const functions = getFunctions();
      const searchCompanies = httpsCallable(functions, 'internalSearchCompanies');
      const response = await searchCompanies({ query: trimmedQuery });
      
      if (response.data?.results) {
        setResults(response.data.results);
        if (response.data.results.length === 0) {
          setError('No se encontraron empresas.');
        }
      }
    } catch (err) {
      console.error('[InternalCompanySearchModal] Error:', err);
      setError('No se pudo realizar la búsqueda.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearch('');
    setResults([]);
    setError(null);
    setSelectedPlan(null);
    setExpandedPlanDropdown(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-2xl">
        <DialogHeader>
          <DialogTitle>Buscar empresa</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Plan Overview Section */}
          <div className="space-y-3 p-4 bg-slate-50 border border-slate-200 rounded">
            <h3 className="font-semibold text-sm text-black">CUENTAS RUTAPP</h3>
            
            {loadingOverview && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Cargando resumen...
              </div>
            )}
            
            {overviewError && (
              <div className="text-xs text-destructive">{overviewError}</div>
            )}
            
            {overview && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-black">
                  Total: {overview.total}
                </div>
                
                <div className="space-y-1">
                  {Object.entries(overview.counts)
                    .sort(([planA], [planB]) => planA.localeCompare(planB))
                    .map(([plan, count]) => (
                      <div key={plan} className="text-xs text-black">
                        {plan}: <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                </div>
                
                {/* Dropdown para seleccionar plan */}
                <div className="relative mt-2">
                  <button
                    onClick={() => setExpandedPlanDropdown(!expandedPlanDropdown)}
                    className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded bg-white hover:bg-slate-50 flex items-center justify-between text-black"
                  >
                    {selectedPlan ? `Plan: ${selectedPlan}` : 'Ver cuentas por plan'}
                    <ChevronDown
                      className={`h-3 w-3 transition-transform ${
                        expandedPlanDropdown ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {expandedPlanDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 rounded shadow-md z-10">
                      {Object.keys(overview.counts)
                        .sort()
                        .map((plan) => (
                          <button
                            key={plan}
                            onClick={() => {
                              setSelectedPlan(plan);
                              setExpandedPlanDropdown(false);
                            }}
                            className={`w-full px-2 py-1.5 text-xs text-left hover:bg-slate-100 text-black ${
                              selectedPlan === plan ? 'bg-slate-50 font-medium' : ''
                            }`}
                          >
                            {plan} ({overview.counts[plan]})
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                
                {/* Lista de cuentas del plan seleccionado */}
                {selectedPlan && (
                  <div className="mt-3 space-y-2 max-h-48 overflow-y-auto border-t border-slate-200 pt-2">
                    {overview.accounts
                      .filter((acc) => acc.effectivePlan === selectedPlan)
                      .map((account) => (
                        <div
                          key={account.workspaceId}
                          className="text-xs bg-white p-2 rounded border border-slate-200 space-y-0.5 text-black"
                        >
                          <div>
                            <span className="font-medium">Empresa:</span>{' '}
                            {account.companyName || 'Sin nombre'}
                          </div>
                          {account.ownerEmail && (
                            <div>
                              <span className="font-medium">Email:</span> {account.ownerEmail}
                            </div>
                          )}
                          <div className="font-mono text-xs text-black">
                            <span className="font-medium">ID:</span> {account.workspaceId}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input y Botón de búsqueda */}
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Email o nombre de empresa"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              disabled={loading}
              className="flex-1"
            />
            <Button
              onClick={handleSearch}
              disabled={loading || !search.trim()}
              className="whitespace-nowrap"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Buscando…
                </>
              ) : (
                'Buscar'
              )}
            </Button>
          </div>

          {/* Mensaje de error */}
          {error && (
            <div className="p-3 bg-destructive/10 text-destructive rounded text-sm">
              {error}
            </div>
          )}

          {/* Resultados */}
          {results.length > 0 && (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {results.map((result, idx) => (
                <div
                  key={idx}
                  className="p-3 border border-border rounded bg-muted/50 space-y-1 text-sm"
                >
                  {result.companyName ? (
                    <div>
                      <span className="font-medium">Empresa:</span> {result.companyName}
                    </div>
                  ) : (
                    <div>
                      <span className="font-medium">Empresa:</span> Sin nombre
                    </div>
                  )}

                  {(result.email || result.ownerEmail) && (
                    <div>
                      <span className="font-medium">Email:</span>{' '}
                      {result.email || result.ownerEmail}
                    </div>
                  )}

                  {result.uid && (
                    <div className="font-mono text-xs text-muted-foreground">
                      <span className="font-medium">UID:</span> {result.uid}
                    </div>
                  )}

                  {result.workspaceId && (
                    <div className="font-mono text-xs text-muted-foreground">
                      <span className="font-medium">Workspace ID:</span> {result.workspaceId}
                    </div>
                  )}

                  {result.effectivePlan ? (
                    <div>
                      <span className="font-medium">Plan:</span> {result.effectivePlan}
                    </div>
                  ) : (
                    <div>
                      <span className="font-medium">Plan:</span> No disponible
                    </div>
                  )}

                  {result.role && (
                    <div>
                      <span className="font-medium">Rol:</span> {result.role}
                    </div>
                  )}

                  {result.status && (
                    <div className="text-xs text-muted-foreground pt-1">
                      Status: {result.status}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
